import React, { useState, useEffect, useRef, useCallback } from 'react';
import './GameStyles.css';

const MotionGame = () => {
  const [level, setLevel] = useState(1);
  const [points, setPoints] = useState(0);
  const [time, setTime] = useState(240); // 4 minutes total
  const [gameStarted, setGameStarted] = useState(false);
  const [grid, setGrid] = useState([]);
  const [selectedCell, setSelectedCell] = useState(null);
  const [validMoves, setValidMoves] = useState([]);
  const [moves, setMoves] = useState(0);
  const [musicEnabled, setMusicEnabled] = useState(false);
  const [gamePaused, setGamePaused] = useState(false);
  const [, setBallPos] = useState(null);
  const [goalPos, setGoalPos] = useState(null);

  const timerRef = useRef(null);
  const audioRef = useRef(null);

  // Cell types
  const EMPTY = '';
  const BALL = 'start';
  const GOAL = 'goal';
  const OBSTACLE = 'obstacle';
  const PURPLE = 'purple';
  const BLUE = 'blue';
  const GOLDEN = 'golden';
  const GREEN = 'green';
  const DARKBLUE = 'darkblue';

  // Level definitions
  const levels = {
    1: {
      grid: [
        [EMPTY, EMPTY, EMPTY],
        [EMPTY, EMPTY, GOAL],
        [OBSTACLE, BLUE, OBSTACLE],
        [EMPTY, EMPTY, BALL],
        [EMPTY, EMPTY, EMPTY],
      ],
      name: 'Easy Start'
    },
    2: {
      grid: [
        [EMPTY, EMPTY, OBSTACLE],
        [BALL, PURPLE, GOAL],
        [OBSTACLE, PURPLE, OBSTACLE],
        [BLUE, BLUE, BLUE],
        [EMPTY, EMPTY, EMPTY],
      ],
      name: 'Move the Blocks'
    },
    3: {
      grid: [
        [EMPTY, EMPTY, GOAL],
        [GOLDEN, PURPLE, PURPLE],
        [GOLDEN, PURPLE, PURPLE],
        [EMPTY, BALL, EMPTY],
        [EMPTY, EMPTY, EMPTY],
      ],
      name: 'Clear the Path'
    },
    4: {
      grid: [
        [EMPTY, OBSTACLE, OBSTACLE],
        [BALL, EMPTY, EMPTY],
        [PURPLE, PURPLE, OBSTACLE],
        [PURPLE, BLUE, BLUE],
        [GOAL, EMPTY, EMPTY],
      ],
      name: 'Strategic Moves'
    },
    5: {
      grid: [
        [EMPTY, EMPTY, EMPTY, EMPTY],
        [BALL, BLUE, BLUE, EMPTY],
        [OBSTACLE, BLUE, BLUE, EMPTY],
        [EMPTY, EMPTY, EMPTY, OBSTACLE],
        [GOLDEN, PURPLE, PURPLE, GOLDEN],
        [GOLDEN, EMPTY, EMPTY, GOAL],
      ],
      name: 'Advanced Challenge'
    }
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const handleTimerEnd = useCallback(() => {
    alert(`Time's up! Your final score: ${points}`);
    setGameStarted(false);
    setLevel(1);
    setPoints(0);
  }, [points]);

  useEffect(() => {
    if (gameStarted && !gamePaused) {
      timerRef.current = setInterval(() => {
        setTime((prev) => {
          if (prev <= 1) {
            handleTimerEnd();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [gameStarted, gamePaused, handleTimerEnd]);

  const handleStartGame = () => {
    setGameStarted(true);
    setLevel(1);
    setPoints(0);
    setTime(240);
    loadLevel(1);
  };

  const loadLevel = (levelNum) => {
    const levelData = levels[levelNum];
    if (!levelData) {
      alert(`Congratulations! You completed all levels! Final score: ${points}`);
      setGameStarted(false);
      return;
    }

    const newGrid = levelData.grid.map(row => [...row]);
    setGrid(newGrid);
    setMoves(0);
    setSelectedCell(null);
    setValidMoves([]);

    // Find ball and goal positions
    for (let row = 0; row < newGrid.length; row++) {
      for (let col = 0; col < newGrid[row].length; col++) {
        if (newGrid[row][col] === BALL) {
          setBallPos({ row, col });
        }
        if (newGrid[row][col] === GOAL) {
          setGoalPos({ row, col });
        }
      }
    }
  };

  const getCellColor = (cellType) => {
    const colors = {
      [PURPLE]: '#8A4188',
      [BLUE]: '#3BA9E1',
      [GOLDEN]: '#FFAB40',
      [GREEN]: '#A5D329',
      [DARKBLUE]: '#0000FF',
      [OBSTACLE]: '#555',
      [BALL]: '#7E899C',
      [GOAL]: '#7E899C',
    };
    return colors[cellType] || 'transparent';
  };

  const getCellImage = (cellType) => {
    if (cellType === BALL) return '/assets/motion/images/start.png';
    if (cellType === GOAL) return '/assets/motion/images/goal.png';
    if (cellType === OBSTACLE) return '/assets/motion/images/obsticle.png';
    if (cellType === PURPLE) return '/assets/motion/images/purpleSqaure.png';
    if (cellType === BLUE) return '/assets/motion/images/blueSqaure.png';
    if (cellType === GOLDEN) return '/assets/motion/images/square.png';
    if (cellType === GREEN) return '/assets/motion/images/green.png';
    if (cellType === DARKBLUE) return '/assets/motion/images/DarkBlueSqaure.png';
    return null;
  };

  const isMoveable = (cellType) => {
    return cellType === BALL || cellType === PURPLE || cellType === BLUE ||
           cellType === GOLDEN || cellType === GREEN || cellType === DARKBLUE;
  };

  // Find all cells that belong to the same block (connected cells of same color)
  const getBlockCells = (row, col) => {
    const cellType = grid[row][col];
    if (!cellType || cellType === EMPTY || cellType === OBSTACLE || cellType === GOAL) {
      return [];
    }

    const visited = new Set();
    const blockCells = [];
    const queue = [{ row, col }];

    while (queue.length > 0) {
      const current = queue.shift();
      const key = `${current.row},${current.col}`;

      if (visited.has(key)) continue;
      visited.add(key);

      if (current.row < 0 || current.row >= grid.length ||
          current.col < 0 || current.col >= grid[0].length) {
        continue;
      }

      if (grid[current.row][current.col] === cellType) {
        blockCells.push(current);

        // Check all 4 directions for adjacent cells of same type
        queue.push({ row: current.row - 1, col: current.col }); // up
        queue.push({ row: current.row + 1, col: current.col }); // down
        queue.push({ row: current.row, col: current.col - 1 }); // left
        queue.push({ row: current.row, col: current.col + 1 }); // right
      }
    }

    return blockCells;
  };

  const getValidMoves = (row, col) => {
    const cellType = grid[row][col];
    const isBall = cellType === BALL;

    // For the ball, use simple single-cell movement
    if (isBall) {
      const moves = [];
      const directions = [
        { dr: -1, dc: 0 }, // up
        { dr: 1, dc: 0 },  // down
        { dr: 0, dc: -1 }, // left
        { dr: 0, dc: 1 },  // right
      ];

      for (const dir of directions) {
        const newRow = row + dir.dr;
        const newCol = col + dir.dc;

        if (newRow >= 0 && newRow < grid.length &&
            newCol >= 0 && newCol < grid[0].length) {
          const targetCell = grid[newRow][newCol];
          if (targetCell === EMPTY || targetCell === GOAL) {
            moves.push({ row: newRow, col: newCol, direction: dir });
          }
        }
      }
      return moves;
    }

    // For blocks, check if entire block can move in each direction
    const blockCells = getBlockCells(row, col);
    const validDirections = [];
    const directions = [
      { dr: -1, dc: 0, name: 'up' }, // up
      { dr: 1, dc: 0, name: 'down' },  // down
      { dr: 0, dc: -1, name: 'left' }, // left
      { dr: 0, dc: 1, name: 'right' },  // right
    ];

    for (const dir of directions) {
      let canMove = true;

      // Check if ALL cells in the block can move in this direction
      for (const cell of blockCells) {
        const newRow = cell.row + dir.dr;
        const newCol = cell.col + dir.dc;

        // Check bounds
        if (newRow < 0 || newRow >= grid.length ||
            newCol < 0 || newCol >= grid[0].length) {
          canMove = false;
          break;
        }

        const targetCell = grid[newRow][newCol];

        // Target must be empty OR part of the same block (moving into itself)
        const isPartOfBlock = blockCells.some(bc => bc.row === newRow && bc.col === newCol);
        if (targetCell !== EMPTY && !isPartOfBlock) {
          canMove = false;
          break;
        }
      }

      if (canMove) {
        validDirections.push(dir);
      }
    }

    return validDirections;
  };

  const handleCellClick = (row, col) => {
    const cellType = grid[row][col];
    const isBallSelected = selectedCell && grid[selectedCell.row][selectedCell.col] === BALL;

    // If ball is selected and clicking on empty cell OR goal
    if (isBallSelected && (cellType === EMPTY || cellType === GOAL)) {
      // For ball, check if clicking on adjacent empty cell or goal
      if (validMoves.some(m => m.row === row && m.col === col)) {
        movePiece(selectedCell.row, selectedCell.col, row, col);
        setSelectedCell(null);
        setValidMoves([]);
      }
      return;
    }

    // If clicking on an empty cell and we have a block selected
    if (selectedCell && cellType === EMPTY) {
      // Check if this is a valid move direction
      const isBall = grid[selectedCell.row][selectedCell.col] === BALL;

      if (isBall) {
        // For ball, check if clicking on adjacent empty cell
        if (validMoves.some(m => m.row === row && m.col === col)) {
          movePiece(selectedCell.row, selectedCell.col, row, col);
          setSelectedCell(null);
          setValidMoves([]);
        }
      } else {
        // For blocks, determine direction and move if valid
        const rowDiff = row - selectedCell.row;
        const colDiff = col - selectedCell.col;

        // Check if clicking on adjacent cell in a valid direction
        if ((Math.abs(rowDiff) === 1 && colDiff === 0) ||
            (Math.abs(colDiff) === 1 && rowDiff === 0)) {
          const direction = { dr: Math.sign(rowDiff), dc: Math.sign(colDiff) };

          // Check if this direction is valid
          if (validMoves.some(dir => dir.dr === direction.dr && dir.dc === direction.dc)) {
            moveBlock(selectedCell.row, selectedCell.col, direction);
            setSelectedCell(null);
            setValidMoves([]);
          }
        }
      }
      return;
    }

    // If clicking on a moveable piece, select it
    if (isMoveable(cellType)) {
      setSelectedCell({ row, col, type: cellType });
      setValidMoves(getValidMoves(row, col));
    } else {
      setSelectedCell(null);
      setValidMoves([]);
    }
  };

  const movePiece = (fromRow, fromCol, toRow, toCol) => {
    const newGrid = grid.map(row => [...row]);
    const pieceType = newGrid[fromRow][fromCol];

    newGrid[toRow][toCol] = pieceType;
    newGrid[fromRow][fromCol] = EMPTY;

    setGrid(newGrid);
    setMoves(prev => prev + 1);

    // Update ball position if ball was moved
    if (pieceType === BALL) {
      setBallPos({ row: toRow, col: toCol });

      // Check if ball reached goal
      if (toRow === goalPos.row && toCol === goalPos.col) {
        handleLevelComplete();
      }
    }
  };

  const moveBlock = (row, col, direction) => {
    const blockCells = getBlockCells(row, col);
    const newGrid = grid.map(row => [...row]);
    const blockType = grid[row][col];

    // First, clear all cells of the block
    for (const cell of blockCells) {
      newGrid[cell.row][cell.col] = EMPTY;
    }

    // Then, place the block in its new position
    for (const cell of blockCells) {
      const newRow = cell.row + direction.dr;
      const newCol = cell.col + direction.dc;
      newGrid[newRow][newCol] = blockType;
    }

    setGrid(newGrid);
    setMoves(prev => prev + 1);
  };

  const handleLevelComplete = () => {
    const bonus = Math.max(0, 50 - moves);
    setPoints(prev => prev + 10 + bonus);

    setTimeout(() => {
      const nextLevel = level + 1;
      if (levels[nextLevel]) {
        setLevel(nextLevel);
        loadLevel(nextLevel);
      } else {
        alert(`Congratulations! You completed all levels! Final score: ${points + 10 + bonus}`);
        setGameStarted(false);
      }
    }, 1000);
  };


  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePause = () => {
    setGamePaused(prev => !prev);
  };

  const toggleMusic = () => {
    setMusicEnabled(prev => !prev);
    if (audioRef.current) {
      if (musicEnabled) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    }
  };

  return (
    <div className="game-container motion-game">
      <audio ref={audioRef} loop>
        <source src="/assets/motion/audio/audio.mp3" type="audio/mpeg" />
      </audio>

      {!gameStarted ? (
        <div className="rules-container">
          <h1 className="game-title">Motion Challenge</h1>
          <div className="rules-content">
            <p>
              Guide the red ball to the black hole by moving colored blocks out of the way!
              Click on a colored block or the ball to select it, then click on an adjacent empty space to move it.
              Plan your moves carefully to create a path to victory!
            </p>
            <h3>How to Play</h3>
            <ul>
              <li>Click on the ball or colored blocks to select them</li>
              <li>Connected blocks of the same color move together as one unit</li>
              <li>Click on adjacent empty spaces to move selected pieces (up, down, left, right)</li>
              <li>Move blocks strategically to clear a path for the ball</li>
              <li>Guide the ball to the black hole to complete the level</li>
              <li>Fewer moves = higher score bonus!</li>
              <li>Complete all levels within 4 minutes</li>
            </ul>
            <button className="play-button" onClick={handleStartGame}>
              Play
            </button>
          </div>
        </div>
      ) : (
        <div className="game-play-area">
          <div className="game-header">
            <div className="control-buttons">
              <button onClick={togglePause} className="control-btn">
                {gamePaused ? '▶️' : '⏸️'}
              </button>
              <button onClick={toggleMusic} className="control-btn">
                {musicEnabled ? '🔊' : '🔇'}
              </button>
            </div>
            <div className="game-stat">Level {level}</div>
            <div className="game-stat">Score: {points}</div>
            <div className="game-stat">Moves: {moves}</div>
            <div className="game-stat">Time: {formatTime(time)}</div>
            <div className="game-name">Motion Game</div>
          </div>

          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${(time / 240) * 100}%` }}></div>
          </div>

          <div className="game-content motion-content">
            <h2 className="level-name">{levels[level]?.name}</h2>

            <div
              className="motion-grid"
              style={{
                gridTemplateColumns: `repeat(${grid[0]?.length || 3}, 1fr)`,
                gridTemplateRows: `repeat(${grid.length}, 1fr)`
              }}
            >
              {grid.map((row, rowIndex) =>
                row.map((cell, colIndex) => {
                  // Check if this cell is part of the selected block
                  let isSelected = false;
                  let isValidMove = false;

                  if (selectedCell) {
                    const isBall = grid[selectedCell.row][selectedCell.col] === BALL;

                    if (isBall) {
                      // For ball, only the clicked cell is selected
                      isSelected = selectedCell.row === rowIndex && selectedCell.col === colIndex;
                      // Show valid moves for ball (including goal)
                      isValidMove = validMoves.some(m => m.row === rowIndex && m.col === colIndex) &&
                                    (cell === EMPTY || cell === GOAL);
                    } else {
                      // For blocks, check if this cell is part of the selected block
                      const blockCells = getBlockCells(selectedCell.row, selectedCell.col);
                      isSelected = blockCells.some(bc => bc.row === rowIndex && bc.col === colIndex);

                      // Show valid moves: empty cells adjacent to the block in valid directions
                      if (cell === EMPTY && validMoves.length > 0) {
                        for (const dir of validMoves) {
                          // Check if this empty cell is in a valid direction from any block cell
                          for (const bc of blockCells) {
                            if (bc.row + dir.dr === rowIndex && bc.col + dir.dc === colIndex) {
                              isValidMove = true;
                              break;
                            }
                          }
                          if (isValidMove) break;
                        }
                      }
                    }
                  }

                  const cellImage = getCellImage(cell);
                  const cellColor = getCellColor(cell);

                  return (
                    <div
                      key={`${rowIndex}-${colIndex}`}
                      className={`motion-cell ${isSelected ? 'selected-cell' : ''} ${isValidMove ? 'valid-move' : ''} ${cell === EMPTY ? 'empty' : ''}`}
                      style={{ backgroundColor: cell !== EMPTY ? cellColor : '#f0f0f0' }}
                      onClick={() => handleCellClick(rowIndex, colIndex)}
                    >
                      {cellImage && <img src={cellImage} alt={cell} className="cell-image" />}
                    </div>
                  );
                })
              )}
            </div>

            <div className="motion-instructions">
              {selectedCell ? (
                grid[selectedCell.row][selectedCell.col] === BALL ? (
                  <p>🎯 Click on a highlighted empty space to move the ball</p>
                ) : (
                  <p>🎯 Click on a highlighted empty space to move the entire block</p>
                )
              ) : (
                <p>👆 Click on the ball or a colored block to select it</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MotionGame;
